import React, { SetStateAction, Dispatch, memo } from "react";
import styled from "@emotion/styled";
import { LegendLabels } from "@atlas-int/data";
import { LegendLabel } from "./LegendLabel";
import Label, { CategoryDatum } from "./LegendItem";
import { breakPoints } from "@atlas-int/tokens/layout";
import { backgroundMedium, baseColor } from "./styles";
import { fadeAndSlideDown } from "@atlas-int/tokens";
import { spacing, fontWeight, fontSizeValues, duration } from "@atlas-int/tokens";

const ReloadSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15.9 16"><path d="M15.9,7V1.2l-1.8,1.8C12.7,1.1,10.5,0,8,0C3.6,0,0,3.6,0,8c0,4.4,3.6,8,8,8c4.1,0,7.4-3.1,7.9-7h-1.6c-0.5,3.1-3.1,5.4-6.3,5.4c-3.5,0-6.4-2.9-6.4-6.4c0-3.5,2.9-6.4,6.4-6.4c2,0,3.9,1,5,2.5L10.1,7H15.9z"/></svg>`;

const ReloadIcon = styled.div`
  width: clamp(1.15rem, 1.75vw, 1.5rem);
  height: clamp(1.15rem, 1.75vw, 1.5rem);
  margin-right: 0.2rem;
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;

    path {
      fill: ${baseColor};
    }
  }
`;

const RootBase = styled.div`
  grid-row: 3;
  display: flex;
  align-items: center;
  @media ${breakPoints.small} {
    grid-row: 4;
  }
`;

const StandardRoot = styled(RootBase)`
  grid-column: 1;
  justify-content: center;
`;

const FullWidthRoot = styled(RootBase)`
  grid-column: 1 / -1;
`;

const StandardContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  box-sizing: border-box;
  padding: ${spacing[8]} 0;

  @media (max-width: 1325px) {
    padding: ${spacing[4]} 0;
  }
`;

const FullWidthContent = styled(StandardContent)`
  @media ${breakPoints.small} {
    padding-right: 0;
  }
`;

const StaticContent = styled(StandardContent)`
  pointer-events: none;
`;

const ResetLabelsButtonContainer = styled.div`
  pointer-events: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ResetLabelsButton = styled.button`
  pointer-events: all;
  cursor: pointer;
  border: none;
  animation: ${fadeAndSlideDown} ${duration.base} ease-in-out 1 forwards;
  color: ${baseColor};
  background-color: ${backgroundMedium};
  font-size: clamp(0.45rem, 0.65vw, 0.5rem);
  font-weight: 600;
  outline-color: ${backgroundMedium};
  transition: outline 0.1s ease-in-out;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  max-width: 5.5rem;
  width: min-content;
  min-height: 16px;
  height: 100%;
  margin: 0.1rem;
  margin-left: 0.5rem;
  padding: 0.15rem 0.5rem;
  text-align: left;
  text-transform: uppercase;

  &:hover,
  &:focus {
    outline: solid 2px ${backgroundMedium};
  }
`;

interface BaseProps {
  categories: CategoryDatum[];
  fullWidth?: boolean;
  legendLabel?: LegendLabels | undefined;
  smallFont?: boolean;
}
type Props = BaseProps &
  (
    | {
        allowToggle: true;
        hiddenCategories: string[];
        setHiddenCategories: Dispatch<SetStateAction<string[]>>;
        resetText: string;
      }
    | {
        allowToggle: false;
      }
  );

const CategoryLabels = (props: Props) => {
  const { categories, fullWidth, legendLabel, smallFont = false } = props;

  let output: React.ReactElement<any>;
  if (props.allowToggle) {
    const { hiddenCategories, setHiddenCategories, resetText } = props;

    const toggleCategory = (valueToToggle: string) => {
      let newHiddenCategories = [...hiddenCategories];
      let valueIsHidden = newHiddenCategories.find(
        (category) => category === valueToToggle,
      );
      if (valueIsHidden) {
        let hiddenIndex = newHiddenCategories.indexOf(valueToToggle);
        newHiddenCategories.splice(hiddenIndex, 1);
      } else if (!valueIsHidden) {
        newHiddenCategories.push(valueToToggle);
      }
      if (newHiddenCategories.length === categories.length) {
        newHiddenCategories = [];
      }
      setHiddenCategories(newHiddenCategories);
    };

    const isolateCategory = (valueToIsolate: string) => {
      if (
        hiddenCategories.length == categories.length - 1 &&
        !hiddenCategories.includes(valueToIsolate)
      ) {
        resetCategories();
      } else {
        let newHiddenCategories = categories
          .filter((category) => category.id !== valueToIsolate)
          .map((category) => category.id);
        setHiddenCategories(newHiddenCategories);
      }
    };

    const resetCategories = () => {
      setHiddenCategories([]);
    };

    const labels = categories.map((category) => {
      const isHidden = !!hiddenCategories.find((id) => id === category.id);
      const isIsolated =
        hiddenCategories.length === categories.length - 1 && !isHidden;
      return (
        <Label
          key={"sector-label-" + category.id}
          category={category}
          toggleCategory={() => toggleCategory(category.id)}
          isolateCategory={() => isolateCategory(category.id)}
          isHidden={isHidden}
          isIsolated={isIsolated}
        />
      );
    });

    const resetButton = hiddenCategories.length ? (
      <ResetLabelsButtonContainer>
        <ResetLabelsButton
          onClick={resetCategories}
          title={resetText}
          aria-label={resetText}
        >
          <ReloadIcon dangerouslySetInnerHTML={{ __html: ReloadSvg }} />
          {resetText}
        </ResetLabelsButton>
      </ResetLabelsButtonContainer>
    ) : null;

    output = (
      <>
        {labels}
        {resetButton}
      </>
    );
  } else {
    const labels = categories.map((category) => (
      <Label
        key={"sector-label-" + category.id}
        category={category}
        isHidden={false}
        isIsolated={false}
        smallFont={smallFont}
      />
    ));

    output = <>{labels}</>;
  }

  const Root = fullWidth ? FullWidthRoot : StandardRoot;

  let Content;
  if (!fullWidth) {
    Content = props.allowToggle ? StandardContent : StaticContent;
  } else {
    Content = props.allowToggle ? FullWidthContent : StaticContent;
  }

  return (
    <Root>
      {legendLabel && (
        <LegendLabel style={{ fontSize: smallFont ? fontSizeValues.xs : fontSizeValues.base }}>
          {legendLabel}
        </LegendLabel>
      )}
      <Content>{output}</Content>
    </Root>
  );
};

export default memo(CategoryLabels);
